import throttle from 'lodash/throttle';
import { useState, useEffect, useCallback } from 'react';
import { getPlanViewBoxValues, getScaledViewBox } from '..';
export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 2;
const RESIZE_THROTTLE_DELAY = 100;
export const INITIAL_MOBILE_PADDING = 32;
export const INITIAL_DESKTOP_PADDING = 68;
export const useViewBox = (plan = {}, mobile, containerRef) => {
    const container = containerRef?.current;
    const [viewBox, setViewBox] = useState(null);
    const [pan, setPan] = useState(null);
    const [scale, setScale] = useState(1);
    const [containerBox, setContainerBox] = useState(null);
    const [minZoom, setMinZoom] = useState(MIN_ZOOM);
    const padding = mobile ? INITIAL_MOBILE_PADDING : INITIAL_DESKTOP_PADDING;
    useEffect(() => {
        if (containerRef?.current && !pan) {
            const containerBoundingBox = containerRef.current.getBoundingClientRect();
            const box = getPlanViewBoxValues({ plan, container: containerRef.current, padding });
            const calculatedScale = containerBoundingBox.width / box.width;
            const { centeringX, centeringY } = getScaledViewBox({
                scale: calculatedScale,
                container: containerRef.current,
            });
            setPan({ x: box.x + centeringX, y: box.y + centeringY });
            setScale(calculatedScale);
            setMinZoom(Math.min(MIN_ZOOM, calculatedScale));
        }
    }, [containerRef, plan, mobile, padding]);
    const changeSvgViewBox = useCallback(({ deltaX = 0, deltaY = 0, deltaScale = 0, zoomPosition = null }) => {
        const { x: notCenteredX, y: notCenteredY } = pan;
        let minX = notCenteredX - deltaX;
        let minY = notCenteredY - deltaY;
        const newScale = Math.min(MAX_ZOOM, Math.max(minZoom, scale + deltaScale));
        const { centeringX, centeringY, width: centeringWidth, height: centeringHeight, deltaX: xCorrection, deltaY: yCorrection, } = getScaledViewBox({
            scale: newScale,
            container: containerRef.current,
            customCentering: zoomPosition && deltaScale
                ? {
                    oldScale: scale,
                    zoomPosition,
                }
                : null,
        });
        minX += xCorrection;
        minY += yCorrection;
        setPan({ x: minX, y: minY });
        setScale(newScale);
        minX -= centeringX;
        minY -= centeringY;
        setViewBox({ x: minX, y: minY, width: centeringWidth, height: centeringHeight });
    }, [containerRef, pan, scale, minZoom]);
    useEffect(() => {
        if (!containerBox && container) {
            setContainerBox(containerRef.current.getBoundingClientRect());
        }
        const handleResize = throttle(() => {
            const newBox = containerRef.current.getBoundingClientRect();
            const deltaX = (newBox.width - containerBox.width) / scale;
            const deltaY = (newBox.height - containerBox.height) / scale;
            setContainerBox(newBox);
            changeSvgViewBox({ deltaX, deltaY });
        }, RESIZE_THROTTLE_DELAY);
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [changeSvgViewBox, container, containerBox, containerRef, scale]);
    useEffect(() => {
        if (pan && !viewBox) {
            changeSvgViewBox({ deltaX: 0, deltaY: 0, deltaScale: 0 });
        }
    }, [changeSvgViewBox, pan, viewBox]);
    return {
        ready: Boolean(viewBox),
        viewBox: viewBox ? `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}` : null,
        zoom: scale,
        container,
        changeSvgViewBox,
        minZoom,
    };
};
//# sourceMappingURL=viewbox.js.map